import React, { forwardRef, useId } from "react";

import { FieldLabel } from "src/primitives/Field";
import { Switch as PrimitiveSwitch } from "src/primitives/Switch";
import { cn } from "src/shadcn/lib/utils";

export interface SwitchProps extends Omit<
  React.ComponentProps<typeof PrimitiveSwitch>,
  "children"
> {
  /** Text label displayed next to the switch. */
  label?: string;
  /** Error message displayed below the switch. */
  error?: string;
  /** Helper text displayed below the label. Accepts string or ReactNode. */
  helpText?: React.ReactNode;
  /** Shows a required indicator next to the label. */
  required?: boolean;
  /** Additional class name for the outermost wrapper. */
  className?: string;
  /** Size of the switch. */
  size?: "sm" | "default" | "lg";
  /** Props forwarded to the Label element. */
  labelProps?: React.ComponentProps<typeof FieldLabel>;
}

const Switch = forwardRef<
  React.ComponentRef<typeof PrimitiveSwitch>,
  SwitchProps
>(
  (
    {
      label,
      error,
      helpText,
      required = false,
      className,
      size = "default",
      disabled,
      id: idProp,
      labelProps,
      ...otherProps
    },
    ref
  ) => {
    const generatedId = useId();
    const id = idProp ?? generatedId;
    const errorId = `error_${id}`;
    const helpTextId = `helpText_${id}`;

    const ariaDescribedBy =
      [error ? errorId : null, helpText ? helpTextId : null]
        .filter(Boolean)
        .join(" ") || undefined;

    return (
      <div
        className={cn("inline-flex flex-col gap-1.5", className)}
        data-disabled={disabled || undefined}
        data-invalid={!!error || undefined}
      >
        {/* relative contains Radix's hidden form input (an absolutely
            positioned sibling of the switch button); without a positioned
            ancestor it resolves against the document and can inflate the
            page's scroll height. */}
        <div
          className={cn(
            "relative inline-flex gap-2",
            helpText ? "items-start" : "items-center"
          )}
        >
          <PrimitiveSwitch
            ref={ref}
            id={id}
            size={size}
            disabled={disabled}
            aria-invalid={!!error || undefined}
            aria-describedby={ariaDescribedBy}
            aria-required={required || undefined}
            className={helpText ? "mt-0.5" : undefined}
            {...otherProps}
          />
          {(label || helpText) && (
            <div className="flex flex-col gap-0.5">
              {label && (
                <FieldLabel
                  htmlFor={id}
                  {...labelProps}
                  className={cn(
                    "cursor-pointer",
                    disabled && "cursor-not-allowed opacity-50",
                    labelProps?.className
                  )}
                >
                  {label}
                  {required && (
                    <span aria-hidden="true" className="text-destructive">
                      *
                    </span>
                  )}
                </FieldLabel>
              )}
              {helpText && (
                <p id={helpTextId} className="text-sm text-muted-foreground">
                  {helpText}
                </p>
              )}
            </div>
          )}
        </div>
        {error && (
          <p id={errorId} role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Switch.displayName = "Switch";

export { Switch };
